/**
 * SMS WebSocket connection provider.
 *
 * Mirrors AIM's SocketContext / useSocketService (ADR-049):
 *   - single WS connection for the whole app
 *   - exponential backoff with jitter on unexpected close
 *   - fast-path reconnect for graceful close codes (4001, 4010)
 *   - stop-and-clear session on auth-failure close code (4002)
 *   - `supports_ack: true` capabilities handshake on open
 *   - ACK reply on messages flagged with `requires_ack`
 *   - short-lived ws/ticket exchange so the JWT never lands in
 *     access logs / browser history / referer headers
 *
 * School-wide reunification visibility: the backend broadcasts to the
 * `school-reunification-{school_id}` channel group, but the EverythingConsumer
 * in apps/aegix-aim-backend/server/api/v2/consumers.py has no `action:
 * 'subscribe'` branch (a client-sent subscribe frame is a no-op) and joins no
 * `school-reunification-*` group on connect, so SMS receives reunification
 * events only via the `user-{id}` group the consumer auto-joins. That covers
 * events the backend explicitly targets at the user; school-wide scope waits
 * on a server-side group_add, tracked as a follow-on to AAS-988.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { onTokenRefreshComplete } from '@tiaki/http-client';
import { useAuth } from '@aegix/auth-identity';
import { type SMSUser, getSmsAccessToken } from '../lib/sms-auth';

// ADR-049 reconnection knobs — identical shape to AIM's socket service.
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30_000;
const JITTER_FACTOR = 0.2;
const GRACEFUL_RECONNECT_DELAY_MS = 500;
const HARD_MAX_RETRIES = 50;
const STABLE_CONNECTION_THRESHOLD_MS = 30_000;

// Private-use WS close codes (RFC 6455 4000-4999).
export const WS_CLOSE_CODES = {
  PONG_TIMEOUT: 4000,
  TOKEN_REFRESH: 4001,
  AUTH_FAILED: 4002,
  SERVER_SHUTDOWN: 4010,
} as const;

const GRACEFUL_CLOSE_CODES = new Set<number>([
  WS_CLOSE_CODES.TOKEN_REFRESH,
  WS_CLOSE_CODES.SERVER_SHUTDOWN,
]);

const AUTH_FAILURE_CLOSE_CODES = new Set<number>([WS_CLOSE_CODES.AUTH_FAILED]);

export type ConnectionStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'
  | 'auth_failed';

export interface ConnectionState {
  status: ConnectionStatus;
  lastConnected: Date | null;
  reconnectAttempt: number;
}

type MessageListener = (raw: string) => void;

interface SocketContextValue {
  connectionState: ConnectionState;
  isConnected: boolean;
  isReconnecting: boolean;
  addMessageListener: (listener: MessageListener) => void;
  removeMessageListener: (listener: MessageListener) => void;
}

const SocketContext = createContext<SocketContextValue | null>(null);

function addJitter(delay: number): number {
  const jitter = delay * JITTER_FACTOR * (Math.random() * 2 - 1);
  return Math.round(delay + jitter);
}

function getReconnectDelay(retryCount: number): number {
  const exp = Math.min(BASE_DELAY_MS * Math.pow(2, retryCount), MAX_DELAY_MS);
  return addJitter(exp);
}

/**
 * Resolve the base WS URL (no query string) from runtime env. Returns null
 * when neither NX_SOCKET_URL nor NX_API_URL is set — the provider then
 * stays in `idle` state.
 */
function getWsBaseUrl(): string | null {
  const socketUrl = window.__ENV?.NX_SOCKET_URL;
  if (socketUrl) return socketUrl.replace(/\/+$/, '');

  const apiUrl = window.__ENV?.NX_API_URL;
  if (!apiUrl) return null;

  const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
  return `${wsProtocol}://${apiUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '')}`;
}

/**
 * Exchange the session JWT for a short-lived ticket so the token
 * never ends up in URL logs (nginx access logs, DuploCloud load balancer,
 * browser history, referer headers, Sentry session replays). Returns the
 * ticket string on success, 'auth_failed' if the backend rejects the
 * token, or null if the endpoint is unreachable (network error) so the
 * caller can fall back to the legacy ?token= path.
 *
 * Handling matches AIM's
 *   apps/aegix-aim-react/src/main/services/socket/socket.tsx
 * — identical contract with the backend.
 *
 * Device identity: the socket's `device_id` is copied
 * off the verified access token's signed claim when the ticket is minted
 * (`server/api/v2/ws_ticket.py`) — the claim the LOGIN request's
 * `x-device-id` header established (see `lib/deviceIdHeader.ts`). The
 * ticket exchange deliberately does NOT restate `x-device-id`: the backend
 * ignores a socket's own claim of identity, which is what makes the
 * binding unforgeable. Same for the legacy `?token=` fallback — the token
 * itself carries the claim.
 */
async function exchangeTokenForTicket(
  token: string,
): Promise<{ kind: 'ticket'; ticket: string } | 'auth_failed' | null> {
  const apiBase = (
    window.__ENV?.NX_API_URL ?? window.location.origin
  ).replace(/\/+$/, '');

  try {
    const response = await fetch(`${apiBase}/api/v2/ws/ticket/`, {
      method: 'POST',
      headers: { Authorization: `JWT ${token}` },
    });
    if (response.status === 401) return 'auth_failed';
    if (!response.ok) return null;
    const data = (await response.json()) as { ticket?: string };
    if (typeof data.ticket === 'string' && data.ticket.length > 0) {
      return { kind: 'ticket', ticket: data.ticket };
    }
    return null;
  } catch {
    return null;
  }
}

export interface SocketProviderProps extends PropsWithChildren {
  /**
   * Optional factory for testing — lets tests inject a MockWebSocket without
   * touching the global. Defaults to the native WebSocket constructor.
   */
  socketFactory?: (url: string) => WebSocket;
}

export function SocketProvider({ children, socketFactory }: SocketProviderProps) {
  // Read the raw JWT directly from the lib's auth state via
  // `useAuth().state.jwt` rather than localStorage. The persistence
  // effect (lib-side) writes to localStorage on the SAME commit that
  // sets state.jwt, but effects run child-first — by the time this
  // provider's effect runs `connect()`, the parent AuthProvider's
  // persistence effect hasn't fired yet, so `localStorage.getItem`
  // returns stale (or null) values. Reading from state is
  // synchronous with the render that flipped the user to
  // authenticated. Falls back to localStorage when state.jwt is empty
  // (cookie-only auth) or the legacy `__cookie__` sentinel, so the WS
  // still has a real token to send.
  const auth = useAuth<SMSUser>();
  const stateToken = (() => {
    switch (auth.state.status) {
      case 'authenticated':
      case 'refreshing':
        return auth.state.jwt && auth.state.jwt !== '__cookie__'
          ? auth.state.jwt
          : null;
      default:
        return null;
    }
  })();
  const isLoggedIn =
    auth.state.status === 'authenticated' ||
    auth.state.status === 'refreshing';

  const socketRef = useRef<WebSocket | null>(null);
  const listenersRef = useRef<MessageListener[]>([]);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stabilityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);

  const [connectionState, setConnectionState] = useState<ConnectionState>({
    status: 'idle',
    lastConnected: null,
    reconnectAttempt: 0,
  });

  const clearReconnectTimer = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  }, []);

  const clearStabilityTimer = useCallback(() => {
    if (stabilityTimerRef.current) {
      clearTimeout(stabilityTimerRef.current);
      stabilityTimerRef.current = null;
    }
  }, []);

  // Hold the latest JWT in a ref so `connect()` always sees
  // the current token without re-creating the callback on every
  // token rotation. Two sources contribute:
  //
  //   1. `stateToken` (above) — the JWT inside the lib's auth state.
  //      This is the canonical source and updates synchronously with
  //      the render that flips `authenticated`. Token rotations via
  //      `actions.refresh()` also update it.
  //
  //   2. localStorage `aegix_sms_token` — the legacy SMS key that
  //      `@tiaki/http-client` writes when it rotates a token via the
  //      `x-refreshed-token` response header. The lib's
  //      auth state doesn't see that rotation (it happens entirely
  //      inside http-client's middleware), so the ref falls back to
  //      localStorage so a fresh ticket exchange uses the rotated
  //      token. The `onTokenRefreshComplete` listener below schedules
  //      a graceful WS reconnect (4001) when http-client rotates,
  //      which re-runs `connect()` and picks up the new value here.
  //
  // With `stateToken` in a ref, token rotations through the lib
  // don't tear down the socket: the main effect's
  // deps only change on login/logout (`isLoggedIn`), not on every
  // token mutation.
  const tokenRef = useRef<string | null>(stateToken);
  tokenRef.current = stateToken;

  const connectRef = useRef<() => void>(() => undefined);
  const socketFactoryRef = useRef(socketFactory);
  socketFactoryRef.current = socketFactory;

  const readStoredToken = useCallback((): string | null => {
    if (tokenRef.current) return tokenRef.current;
    // Read the rotated JWT through the SMS auth accessor
    // (raw token in `aegix_sms_token`) rather than localStorage directly.
    return getSmsAccessToken();
  }, []);

  const connect = useCallback(() => {
    // `connect` runs async internally so the caller can treat it like a
    // normal imperative trigger; unhandled errors in the async body
    // surface through onclose → reconnectWithBackoff.
    void (async () => {
      const storedToken = readStoredToken();
      if (!storedToken) return;

      const wsBase = getWsBaseUrl();
      if (!wsBase) return;

      clearReconnectTimer();

      // Try the ticket endpoint first — keeps the JWT out of the
      // WebSocket URL. Fall through to ?token= only on network error; a
      // 401 short-circuits into auth_failed without opening a socket.
      let wsUrl = `${wsBase}/api/v2/ws/?token=${storedToken}`;
      const ticketResult = await exchangeTokenForTicket(storedToken);
      if (ticketResult === 'auth_failed') {
        setConnectionState({
          status: 'auth_failed',
          lastConnected: null,
          reconnectAttempt: 0,
        });
        retryCountRef.current = 0;
        return;
      }
      if (ticketResult && ticketResult.kind === 'ticket') {
        wsUrl = `${wsBase}/api/v2/ws/?ticket=${ticketResult.ticket}`;
      }

      // A logout that raced with the ticket fetch may have cleared
      // localStorage by now — re-check before opening the socket so
      // we don't connect with stale credentials. The parent effect
      // observes `useIsAuthenticated()` and also tears down on
      // logout, but that fires on the next render cycle.
      if (!readStoredToken()) return;

      if (socketRef.current) {
        try {
          socketRef.current.close();
        } catch {
          /* ignore */
        }
        socketRef.current = null;
      }

      const factory = socketFactoryRef.current ?? ((u: string) => new WebSocket(u));
      const socket = factory(wsUrl);

      // Attach close BEFORE open so failed connection attempts also trigger reconnect.
      socket.onclose = (event: CloseEvent) => {
        clearStabilityTimer();
        socketRef.current = null;

        if (AUTH_FAILURE_CLOSE_CODES.has(event.code)) {
          setConnectionState({
            status: 'auth_failed',
            lastConnected: null,
            reconnectAttempt: 0,
          });
          retryCountRef.current = 0;
          return;
        }

        if (GRACEFUL_CLOSE_CODES.has(event.code)) {
          retryCountRef.current = 0;
          setConnectionState((prev) => ({
            ...prev,
            status: 'connecting',
            reconnectAttempt: 0,
          }));
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            connectRef.current();
          }, GRACEFUL_RECONNECT_DELAY_MS);
          return;
        }

        // Unexpected close → exponential backoff.
        if (retryCountRef.current >= HARD_MAX_RETRIES) {
          setConnectionState((prev) => ({ ...prev, status: 'disconnected' }));
          return;
        }

        const delay = getReconnectDelay(retryCountRef.current);
        setConnectionState((prev) => ({
          ...prev,
          status: 'reconnecting',
          reconnectAttempt: retryCountRef.current + 1,
        }));
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          retryCountRef.current += 1;
          connectRef.current();
        }, delay);
      };

      socket.onopen = () => {
        socketRef.current = socket;

        clearStabilityTimer();
        stabilityTimerRef.current = setTimeout(() => {
          stabilityTimerRef.current = null;
          retryCountRef.current = 0;
        }, STABLE_CONNECTION_THRESHOLD_MS);

        setConnectionState({
          status: 'connected',
          lastConnected: new Date(),
          reconnectAttempt: 0,
        });

        // Declare ACK support BEFORE processing messages so the
        // server replays pending critical events (reunification.activated
        // et al.) that arrived while we were offline.
        try {
          socket.send(
            JSON.stringify({
              type: 'capabilities',
              supports_ack: true,
              client: 'sms-web',
              version: '1.0.0',
            }),
          );
        } catch {
          /* socket closed mid-handshake — onclose will handle it */
        }

        // NOTE: no subscribe frame is sent here. `EverythingConsumer`
        // auto-joins `user-{user_id}` on accept, which is enough for
        // backend broadcasts that target the user directly. School-wide
        // reunification scope (`school-reunification-{school_id}`)
        // requires a backend-side group_add — tracked as a follow-on to
        // AAS-988. Sending a client `{action: 'subscribe', ...}` frame
        // is a no-op against the current consumer and would create a
        // false sense of coverage.
      };

      socket.onmessage = (ev: MessageEvent) => {
        const raw = typeof ev.data === 'string' ? ev.data : '';
        if (!raw) return;

        // Reply with ACK so the server stops replaying.
        try {
          const parsed = JSON.parse(raw);
          if (parsed?.requires_ack && parsed?.id) {
            socket.send(JSON.stringify({ type: 'ack', message_id: parsed.id }));
          }
        } catch {
          /* non-JSON frame — still deliver to listeners */
        }

        for (const listener of listenersRef.current) {
          try {
            listener(raw);
          } catch (err) {
            console.error('[SocketProvider] listener error:', err);
          }
        }
      };

      socket.onerror = () => {
        /* onclose fires right after — reconnect is handled there */
      };
    })();
  }, [clearReconnectTimer, clearStabilityTimer, readStoredToken]);

  connectRef.current = connect;

  // Detach handlers BEFORE closing so the cleanup-initiated close does
  // not re-enter the reconnect-backoff branch (code 1000 falls through
  // to the "unexpected close" path). Prior to this guard, an unmount —
  // normal in tests, logout, hot-reload — left a pending setTimeout
  // that resolved after the owning effect was gone and spawned a new
  // WebSocket against the stale factory.
  const detachAndClose = useCallback((socket: WebSocket | null) => {
    if (!socket) return;
    socket.onopen = null;
    socket.onclose = null;
    socket.onmessage = null;
    socket.onerror = null;
    try {
      socket.close();
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      clearReconnectTimer();
      clearStabilityTimer();
      detachAndClose(socketRef.current);
      socketRef.current = null;
      retryCountRef.current = 0;
      setConnectionState({ status: 'idle', lastConnected: null, reconnectAttempt: 0 });
      return;
    }

    setConnectionState((prev) => ({ ...prev, status: 'connecting' }));
    connect();

    return () => {
      clearReconnectTimer();
      clearStabilityTimer();
      detachAndClose(socketRef.current);
      socketRef.current = null;
    };
  }, [isLoggedIn, connect, clearReconnectTimer, clearStabilityTimer, detachAndClose]);

  // Subscribe to the lib's token-refresh listener so the socket
  // reconnects with the rotated JWT instead of staying open with a stale
  // one. Closing with WS_CLOSE_CODES.TOKEN_REFRESH (4001) routes through
  // GRACEFUL_CLOSE_CODES → fast-path reconnect (≤500ms) which re-runs
  // exchangeTokenForTicket against the freshly-stored token. This is the
  // first time SMS WS survives a backend-initiated token rotation without
  // dropping the user — the named harm ADR 113 opens with.
  useEffect(() => {
    const unsubscribe = onTokenRefreshComplete(() => {
      const live = socketRef.current;
      if (live && live.readyState === WebSocket.OPEN) {
        try {
          live.close(WS_CLOSE_CODES.TOKEN_REFRESH, 'token-rotated');
        } catch {
          /* close raced with another lifecycle event — onclose handles it */
        }
      }
    });
    return unsubscribe;
  }, []);

  const addMessageListener = useCallback((listener: MessageListener) => {
    listenersRef.current.push(listener);
  }, []);

  const removeMessageListener = useCallback((listener: MessageListener) => {
    const idx = listenersRef.current.indexOf(listener);
    if (idx !== -1) listenersRef.current.splice(idx, 1);
  }, []);

  const value = useMemo<SocketContextValue>(
    () => ({
      connectionState,
      isConnected: connectionState.status === 'connected',
      isReconnecting: connectionState.status === 'reconnecting',
      addMessageListener,
      removeMessageListener,
    }),
    [connectionState, addMessageListener, removeMessageListener],
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket(): SocketContextValue {
  const ctx = useContext(SocketContext);
  if (!ctx) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return ctx;
}

export function useSocketMessage(handler: MessageListener) {
  const { addMessageListener, removeMessageListener } = useSocket();
  useEffect(() => {
    addMessageListener(handler);
    return () => removeMessageListener(handler);
  }, [handler, addMessageListener, removeMessageListener]);
}

export default SocketContext;
